import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import classNames from "classnames/bind";
import styles from "./AIChatWidget.module.scss";
import { useGetAIChatMessagesQuery, useGetAIChatUsageQuery } from "../../api/aiChatApi";
import { useAIChatStream } from "../../hooks/useAIChatStream";
import { useAIChatSTT } from "../../hooks/useAIChatSTT";
import { aiChatApi } from "../../api/aiChatApi";
import { useAppDispatch } from "../../store";
import { AI_CHAT_LIMITS, type AIChatMessage } from "@nicsmith/shared_types";
import type { RootState } from "../../store";
import AIBotIcon from "./AIBotIcon";
import { useAIChatPageContext } from "./AIChatContext";
import ReactMarkdown from "react-markdown";
import UpgradeModal from "../common/upgrade-modal/UpgradeModal";

const cx = classNames.bind(styles);

// SVG Icons
const CloseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const SendIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);

const MicIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
    <line x1="12" y1="19" x2="12" y2="23" />
    <line x1="8" y1="23" x2="16" y2="23" />
  </svg>
);

const AIChatWidget: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { moduleId, moduleName, exerciseContext } = useAIChatPageContext();
  const user = useSelector((state: RootState) => state.session.user);
  const isPremium = user?.subscription?.tier === "PREMIUM";
  const isMessagesPage = location.pathname.endsWith("/messages") || location.pathname.includes("/messages/");

  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [pendingUserMessage, setPendingUserMessage] = useState<string | null>(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // API
  const { data: chatData, refetch: refetchMessages } = useGetAIChatMessagesQuery(
    { moduleId },
    { skip: !isOpen }
  );
  const { data: usageData } = useGetAIChatUsageQuery(undefined, { skip: !isOpen });

  // Streaming
  const { isStreaming, streamedContent, error, errorCode, sendMessage, abort } =
    useAIChatStream(moduleId);

  // Track the final streamed content to show between streaming end and refetch completion.
  // This bridges the gap where isStreaming=false but refetchMessages hasn't returned yet.
  const [completedStreamContent, setCompletedStreamContent] = useState<string | null>(null);
  const streamedContentRef = useRef<string>("");

  // Keep the ref in sync with the latest streamedContent so the onDone closure can read it
  useEffect(() => {
    streamedContentRef.current = streamedContent;
  }, [streamedContent]);

  // STT
  const { isRecording, isTranscribing, startRecording, stopRecording } = useAIChatSTT();

  // Scroll to bottom — only when user is already near the bottom to avoid
  // jarring jumps during message transitions. The threshold is generous
  // (250px) so the chat keeps tracking the bottom even if the user is mid-read
  // a few lines up; if they actively scrolled away to read history, the gap
  // will exceed 250px and we leave them alone.
  const scrollToBottomIfAtBottom = useCallback(
    (behavior: ScrollBehavior = "smooth") => {
      const container = messagesContainerRef.current;
      if (!container) return;
      const threshold = 250;
      const isNearBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight < threshold;
      if (isNearBottom) {
        messagesEndRef.current?.scrollIntoView({ behavior });
      }
    },
    []
  );

  // Force scroll regardless of current position — used when user just sent a
  // message (they expect to see it appear) or when the chat first opens.
  const scrollToBottomForce = useCallback((behavior: ScrollBehavior = "auto") => {
    messagesEndRef.current?.scrollIntoView({ behavior });
  }, []);

  // Scroll to bottom when chat opens or messages first load
  const prevMessageCount = useRef(0);
  useLayoutEffect(() => {
    if (!isOpen) return;
    const count = chatData?.messages?.length ?? 0;
    // Scroll on open (count=0 → messages loaded) or first load
    if (prevMessageCount.current === 0 && count > 0) {
      scrollToBottomForce("auto");
    }
    prevMessageCount.current = count;
  }, [isOpen, chatData?.messages, scrollToBottomForce]);

  // Also scroll instantly when chat opens (even before messages load)
  useLayoutEffect(() => {
    if (isOpen) {
      scrollToBottomForce("auto");
    }
  }, [isOpen, scrollToBottomForce]);

  // Always scroll to bottom when user sends a message — they need to see it
  // appear immediately, regardless of where they were scrolled.
  useLayoutEffect(() => {
    if (pendingUserMessage) {
      scrollToBottomForce("smooth");
    }
  }, [pendingUserMessage, scrollToBottomForce]);

  // Track the live stream as tokens arrive so the chat keeps the latest
  // content in view. Guarded by isAtBottom so a user reading history isn't
  // yanked back down.
  useLayoutEffect(() => {
    if (isStreaming && streamedContent) {
      scrollToBottomIfAtBottom("auto");
    }
  }, [streamedContent, isStreaming, scrollToBottomIfAtBottom]);

  // When the streaming finishes and the AI response gets re-rendered through
  // ReactMarkdown, the rendered DOM ends up taller than the plain-text version
  // that was visible during streaming. The single rAF-based scroll runs against
  // the OLD layout and lands above the final bottom. A MutationObserver on the
  // messages container catches every late mutation (markdown re-render, code-
  // block syntax highlighting, image loads, etc.) and re-aligns the scroll.
  useLayoutEffect(() => {
    if (!isOpen) return;
    const container = messagesContainerRef.current;
    if (!container) return;
    const observer = new MutationObserver(() => {
      scrollToBottomIfAtBottom("auto");
    });
    observer.observe(container, {
      childList: true,
      subtree: true,
      characterData: true,
    });
    return () => observer.disconnect();
  }, [isOpen, scrollToBottomIfAtBottom]);

  // Always scroll to bottom when AI response appears (completedStreamContent set)
  useLayoutEffect(() => {
    if (completedStreamContent) {
      scrollToBottomIfAtBottom("smooth");
    }
  }, [completedStreamContent, scrollToBottomIfAtBottom]);

  // Auto-resize textarea
  const adjustTextarea = useCallback(() => {
    const ta = textareaRef.current;
    if (ta) {
      ta.style.height = "0";
      const newHeight = Math.min(ta.scrollHeight, 100);
      ta.style.height = newHeight + "px";
      ta.style.overflowY = ta.scrollHeight > 100 ? "auto" : "hidden";
    }
  }, []);

  const handleSend = useCallback(async () => {
    const content = inputValue.trim();
    const maxLen = AI_CHAT_LIMITS[isPremium ? "PREMIUM" : "FREE"].max_message_length;
    if (!content || isStreaming || content.length > maxLen) return;

    setInputValue("");
    setPendingUserMessage(content);
    setCompletedStreamContent(null);
    if (textareaRef.current) textareaRef.current.style.height = "auto";

    await sendMessage(
      {
        content,
        exercise_context: exerciseContext,
      },
      async () => {
        // Capture the current streamed content so it stays visible during refetch.
        // Use the ref because streamedContent in the closure may be stale.
        setCompletedStreamContent(streamedContentRef.current);
        // DO NOT clear pendingUserMessage here — the refetch hasn't returned yet,
        // so clearing it would cause the user message to disappear until the server
        // responds with the real message list.
        await refetchMessages();
        // Refetch is complete — the server messages now include both the user message
        // and the AI response. Clear the optimistic/bridge state atomically so there
        // is never a frame where the user message or AI response is missing.
        setPendingUserMessage(null);
        setCompletedStreamContent(null);
        dispatch(aiChatApi.util.invalidateTags(["AIChatUsage", "AIChatSessions"]));
      }
    );
  }, [inputValue, isStreaming, sendMessage, exerciseContext, refetchMessages, dispatch]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== "Enter") return;
    // On real touch-primary devices (phones, tablets) Enter should insert a
    // newline; sending is done via the Send button. Desktop keeps the
    // Slack/Discord convention: Enter sends, Shift+Enter makes a newline.
    //
    // Detect mobile via `(pointer: coarse)` — this is true only when the
    // primary pointer is a finger. The previous heuristic (`ontouchstart` /
    // `maxTouchPoints`) returned true on touch-enabled laptops, Surface
    // devices, and Chrome with touch emulation, breaking Enter-to-send for
    // those users.
    const isMobile =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(pointer: coarse)").matches;
    if (isMobile) return;
    if (e.shiftKey) return;
    e.preventDefault();
    handleSend();
  };

  const handleMicToggle = async () => {
    if (isRecording) {
      const text = await stopRecording();
      if (text) {
        setInputValue((prev) => (prev ? prev + " " + text : text));
        setTimeout(() => {
          adjustTextarea();
          textareaRef.current?.focus();
        }, 0);
      }
    } else {
      await startRecording();
    }
  };

  // Messages: combine loaded + streaming
  const messages: AIChatMessage[] = chatData?.messages || [];
  const displayMessages = [...messages].reverse();

  // Only show the pending user message if the server hasn't returned it yet.
  // This prevents a brief duplicate when refetchMessages resolves (updating displayMessages)
  // but setPendingUserMessage(null) hasn't been processed in the same render.
  const lastDisplayMsg = displayMessages[displayMessages.length - 1];
  const showPendingUserMessage =
    pendingUserMessage !== null &&
    !(lastDisplayMsg?.role === "user" && lastDisplayMsg?.content === pendingUserMessage);

  // Usage bar
  const usagePct = usageData
    ? Math.min(100, Math.round((usageData.tokens_used / usageData.tokens_limit) * 100))
    : 0;
  const usageClass = usagePct >= 90 ? "danger" : usagePct >= 70 ? "warning" : "";

  // Header subtitle
  const subtitle = moduleId > 0 ? moduleName : t("aiChat.generalChat", "General chat");

  if (isMessagesPage) return null;

  if (!isOpen) {
    return (
      <button
        className={styles.fab}
        data-tour="ai-chat-fab"
        onClick={() => setIsOpen(true)}
        aria-label={t("aiChat.openTutor", "Open AI Tutor")}
      >
        <AIBotIcon size={64} />
      </button>
    );
  }

  return (
    <>
      <div className={styles.overlay} onClick={() => { abort(); setIsOpen(false); }} />
      <div className={styles.panel}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerIcon}>
            <AIBotIcon size={64} />
          </div>
          <div className={styles.headerInfo}>
            <h3>{t("aiChat.title", "AI Tutor")}</h3>
            <p>{subtitle}</p>
          </div>
          <button
            className={styles.closeButton}
            onClick={() => { abort(); setIsOpen(false); }}
            aria-label={t("aiChat.close", "Close")}
          >
            <CloseIcon />
          </button>
        </div>

        {/* Usage Bar */}
        {usageData && (
          <div className={styles.usageBar}>
            <div className={styles.usageTrack}>
              <div
                className={cx("usageFill", usageClass)}
                style={{ width: `${usagePct}%` }}
              />
            </div>
            <span className={styles.usageText}>
              {usageData.requests_used}/{usageData.requests_limit} {t("aiChat.requests", "requests")}
            </span>
          </div>
        )}

        {/* Error Banner */}
        {error && (
          <div className={styles.errorBanner}>
            {errorCode === "USAGE_LIMIT_EXCEEDED" ? (
              isPremium ? (
                t("aiChat.usageLimitPremium", "You've reached your monthly limit. Please contact support.")
              ) : (
                <>
                  {t("aiChat.usageLimitFree", "You've reached your monthly limit.")}{" "}
                  <button
                    className={styles.errorUpgradeLink}
                    onClick={() => setShowUpgradeModal(true)}
                  >
                    {t("aiChat.upgradeNow", "Upgrade to Premium")}
                  </button>
                </>
              )
            ) : error.toLowerCase().includes("too long") ? (
              t("aiChat.messageTooLong", "Your message is too long. Please shorten it.")
            ) : (
              error
            )}
          </div>
        )}

        {/* Messages */}
        <div className={styles.messages} ref={messagesContainerRef}>
          {/* Plan info as first message */}
          <div className={cx("message", "messageAssistant", "planNote")}>
            {isPremium
              ? t("aiChat.premiumPlanNote", "You have Premium — 1,000 requests/month and up to 2,000 characters per message.")
              : t("aiChat.freePlanNote", "Free plan: 100 requests/month, 500 characters per message. Upgrade to Premium for 1,000 requests and longer messages.")}
          </div>

          {displayMessages.length === 0 && !showPendingUserMessage && !isStreaming && !completedStreamContent ? (
            <div className={styles.emptyMessages}>
              <AIBotIcon size={128} />
              <p>
                {moduleId > 0
                  ? t("aiChat.welcomeMessage", "Hi! I'm your AI tutor. Ask me anything about this lesson.")
                  : t("aiChat.welcomeMessageGeneral", "Hi! I'm your AI tutor. Ask me anything about German.")}
              </p>
              <span>{t("aiChat.welcomeHint", "Try asking about grammar, vocabulary, or exercises")}</span>
            </div>
          ) : (
            <>
              {displayMessages.map((msg) => (
                <div
                  key={msg.message_id}
                  className={cx("message", {
                    messageUser: msg.role === "user",
                    messageAssistant: msg.role === "assistant",
                  })}
                >
                  {msg.role === "assistant" ? (
                    <ReactMarkdown>{msg.content}</ReactMarkdown>
                  ) : (
                    msg.content
                  )}
                </div>
              ))}
              {showPendingUserMessage && (
                <div className={cx("message", "messageUser")}>
                  {pendingUserMessage}
                </div>
              )}
              {isStreaming && (
                <div className={styles.typingIndicator}>
                  <span /><span /><span />
                </div>
              )}
              {!isStreaming && completedStreamContent && (
                <div className={cx("message", "messageAssistant")}>
                  <ReactMarkdown>{completedStreamContent}</ReactMarkdown>
                </div>
              )}
            </>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Bar */}
        <div className={styles.inputBar}>
          <button
            className={cx("iconButton", "micButton", { recording: isRecording })}
            onClick={handleMicToggle}
            disabled={isStreaming || isTranscribing}
            aria-label={isRecording ? t("aiChat.stopRecording", "Stop recording") : t("aiChat.startRecording", "Start recording")}
          >
            <MicIcon />
          </button>
          <textarea
            ref={textareaRef}
            className={styles.input}
            value={inputValue}
            onChange={(e) => { setInputValue(e.target.value); adjustTextarea(); }}
            onKeyDown={handleKeyDown}
            placeholder={
              isTranscribing
                ? t("aiChat.transcribing", "Transcribing...")
                : t("aiChat.inputPlaceholder", "Ask about this lesson...")
            }
            rows={1}
            disabled={isStreaming || isTranscribing}
          />
          <button
            className={cx("iconButton", "sendButton")}
            onClick={handleSend}
            disabled={!inputValue.trim() || isStreaming || inputValue.trim().length > AI_CHAT_LIMITS[isPremium ? "PREMIUM" : "FREE"].max_message_length}
            aria-label={t("aiChat.send", "Send")}
          >
            <SendIcon />
          </button>
        </div>
        {inputValue.length > AI_CHAT_LIMITS[isPremium ? "PREMIUM" : "FREE"].max_message_length * 0.8 && (
          <div className={cx("charCounter", { overLimit: inputValue.length > AI_CHAT_LIMITS[isPremium ? "PREMIUM" : "FREE"].max_message_length })}>
            {inputValue.length > AI_CHAT_LIMITS[isPremium ? "PREMIUM" : "FREE"].max_message_length
              ? t("aiChat.charOverLimit", "{{over}} characters over limit", { over: inputValue.length - AI_CHAT_LIMITS[isPremium ? "PREMIUM" : "FREE"].max_message_length })
              : `${inputValue.length}/${AI_CHAT_LIMITS[isPremium ? "PREMIUM" : "FREE"].max_message_length}`}
          </div>
        )}
      </div>
      <UpgradeModal isOpen={showUpgradeModal} onClose={() => setShowUpgradeModal(false)} />
    </>
  );
};

export default AIChatWidget;
